import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from './ui/sheet';
import { Badge } from './ui/badge';
import { Station } from '../data/mockData';
import { toast } from 'sonner';

interface EditStationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Omit<Station, 'id' | 'channelCount'>) => void;
  onDelete: () => void;
  station: Station;
}

export function EditStationDrawer({
  isOpen,
  onClose,
  onSave,
  onDelete,
  station,
}: EditStationDrawerProps) {
  const [name, setName] = useState('');
  const [status, setStatus] = useState<'Active' | 'Suspended'>('Active');
  const [errors, setErrors] = useState<{ name?: string }>({});

  useEffect(() => {
    if (isOpen && station) {
      setName(station.name);
      setStatus(station.status);
      setErrors({});
    }
  }, [isOpen, station]);

  const validate = () => {
    const newErrors: { name?: string } = {};

    if (!name.trim()) {
      newErrors.name = 'Station name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      toast.error('Please fix the errors before saving');
      return;
    }

    onSave({
      name: name.trim(),
      code: station.code,
      status,
    });

    toast.success('Station updated successfully');
  };

  const handleStatusToggle = (newStatus: 'Active' | 'Suspended') => {
    setStatus(newStatus);
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl bg-gray-900 border-gray-800 text-white overflow-y-auto p-0"
      >
        <SheetHeader className="px-6 sm:px-8 pt-6 sm:pt-8 pb-5 sm:pb-6 border-b border-gray-800">
          <SheetTitle className="text-2xl font-bold text-white">Edit Station</SheetTitle>
          <SheetDescription className="text-gray-400 mt-2">
            Update station details and status
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="px-6 sm:px-8 py-6 sm:py-8 space-y-6">
          {/* Station Name */}
          <div className="space-y-3 max-w-md">
            <Label htmlFor="name" className="text-sm font-semibold text-white">
              Station Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter station name"
              className={`bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 h-11 ${
                errors.name ? 'border-red-500' : ''
              }`}
            />
            {errors.name && (
              <p className="text-xs text-red-400 mt-1">{errors.name}</p>
            )}
          </div>

          {/* Channel Count (Read-only) */}
          <div className="space-y-3 max-w-md">
            <Label className="text-sm font-semibold text-white">Channels</Label>
            <div className="px-4 py-2 h-11 flex items-center bg-gray-800 border border-gray-700 rounded-md text-gray-400">
              {station.channelCount} channel{station.channelCount !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Status */}
          <div className="space-y-3 max-w-md">
            <Label htmlFor="status" className="text-sm font-semibold text-white">
              Status
            </Label>
            <Select value={status} onValueChange={(v) => setStatus(v as any)}>
              <SelectTrigger
                id="status"
                className="bg-gray-800 border-gray-700 text-white h-11"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-800">
                <SelectItem value="Active" className="text-white hover:text-white focus:bg-gray-800 focus:text-white">
                  Active
                </SelectItem>
                <SelectItem value="Suspended" className="text-white hover:text-white focus:bg-gray-800 focus:text-white">
                  Suspended
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </form>

        {/* Actions */}
        <div className="sticky bottom-0 bg-gray-900 border-t border-gray-800 px-6 sm:px-8 py-4 sm:py-5">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onDelete}
              className="flex-1 border-red-500 bg-red-600 text-white hover:bg-red-500"
            >
              Delete Station
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 border-gray-700 text-black hover:bg-gray-800 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              onClick={handleSubmit}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              Save Changes
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}